import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import QRCode from 'qrcode';
import { jsPDF } from 'jspdf';
import api, { formatApiError } from '@/lib/api';
import { Loader2, Download, FileDown, Crown, Sparkles, QrCode, Phone, MapPin, MessageCircle, Copy, CheckCircle } from 'lucide-react';
import { buildVcard } from '@/lib/vcard';
import { buildBrandedQrPng, doctorInitials } from '@/lib/brandedQr';

/**
 * ClinicKit — TurboCharge-only feature.
 * Generates two QR codes (main DocSite URL + vCard offline contact), an A4
 * printable poster PDF, and a set of sticker PNGs for the doctor to print
 * and display at their clinic.
 */
export default function ClinicKitTab({ doctor: doctorProp, user }) {
  const isTurbo = (user?.tier || doctorProp?.tier) === 'turbo';
  const [doctor, setDoctor] = useState(doctorProp || null);
  const [msg, setMsg] = useState('');
  const [mainQr, setMainQr] = useState('');
  const [vcardQr, setVcardQr] = useState('');
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState('');
  const posterPreviewRef = useRef(null);

  // Fetch full public profile (includes locations) once
  useEffect(() => {
    if (doctor?.locations || !user?.slug) return;
    api.get(`/doctor/${user.slug}`).then(r => setDoctor(r.data)).catch((e) => setMsg(formatApiError(e)));
  }, [user?.slug, doctor?.locations]);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const siteUrl = useMemo(() => `${origin}/dr/${doctor?.slug}?src=clinic-qr`, [origin, doctor?.slug]);
  const siteUrlClean = useMemo(() => `${origin}/dr/${doctor?.slug}`, [origin, doctor?.slug]);
  // The vCard's URL field points to the "/hi" landing — when the patient
  // re-opens the saved contact later they hit the conversion CTA.
  const landingUrl = useMemo(() => `${origin}/dr/${doctor?.slug}/hi`, [origin, doctor?.slug]);

  // Build vCard payload from doctor's public profile
  const vcardText = useMemo(
    () => buildVcard(doctor, landingUrl, { includePhone: !!doctor?.qr_share_phone }),
    [doctor, landingUrl]
  );

  // Generate main + vCard QRs once doctor is loaded.
  useEffect(() => {
    if (!doctor || !isTurbo) return;
    (async () => {
      try {
        const m = await QRCode.toDataURL(siteUrl, { width: 1200, margin: 2, errorCorrectionLevel: 'Q', color: { dark: '#0f172a', light: '#ffffff' } });
        setMainQr(m);
        const v = await QRCode.toDataURL(vcardText, { width: 800, margin: 2, errorCorrectionLevel: 'Q', color: { dark: '#0f172a', light: '#ffffff' } });
        setVcardQr(v);
      } catch (e) {
        setMsg('Failed to generate QR codes: ' + e.message);
      }
    })();
  }, [doctor, isTurbo, siteUrl, vcardText]);

  // Per-clinic Check-In QRs. One per location in the doctor's profile —
  // patient scans inside the clinic, location auto-tags the visit row
  // (no "channel" picker needed). Encodes the location_id in the URL.
  const [clinicQrs, setClinicQrs] = useState({}); // { location_id: dataUrl }

  useEffect(() => {
    if (!doctor || !isTurbo) return;
    const locs = Array.isArray(doctor.locations) ? doctor.locations : [];
    if (locs.length === 0) { setClinicQrs({}); return; }
    (async () => {
      const out = {};
      // Derive a "locality" hint from the address (e.g. "Chandivali" from
      // "Plot 5, Chandivali Farm Road, Powai, Mumbai 400072"). We pick the
      // second comma-separated chunk that isn't a pincode and isn't the city
      // or state — that's the typical Indian-address position for the
      // neighbourhood. Falls back gracefully when address is sparse.
      const localityFor = (loc) => {
        const parts = String(loc.address || '')
          .split(',')
          .map(s => s.trim())
          .filter(Boolean);
        const cityLc = String(loc.city || '').toLowerCase();
        const stateLc = String(loc.state || '').toLowerCase();
        const isPincode = (s) => /^\d{4,7}$/.test(s.replace(/\s/g, ''));
        const candidates = parts.filter(p => {
          const lc = p.toLowerCase();
          return p.length > 2
            && !isPincode(p)
            && lc !== cityLc
            && lc !== stateLc
            && !(cityLc && lc.includes(cityLc))
            && !(stateLc && lc.includes(stateLc));
        });
        // Prefer the 2nd candidate (locality typically sits after street/plot),
        // fall back to the first, then to city.
        return candidates[1] || candidates[0] || loc.city || '';
      };
      for (const l of locs) {
        try {
          const url = `${origin}/dr/${doctor.slug}/checkin?loc=${encodeURIComponent(l.id)}`;
          const locality = localityFor(l);
          const name = l.name || 'Clinic';
          const label = locality && locality.toLowerCase() !== name.toLowerCase()
            ? `${name}, ${locality}`
            : name;
          // Doctor-branded centre badge ("Dr CT") + clinic-name + locality
          // footer + baked-in patient-facing header. ECC=H so the 24% centre
          // badge never breaks scanning even at A6 print sizes.
          const png = await buildBrandedQrPng(url, {
            label,
            initials: `Dr ${doctorInitials(doctor.name)}`,
            topHeader: 'PATIENT SELF-CHECKIN QR',
            benefit: 'DIGITAL HEALTH FILE for same day CHECK-IN only. FREE.',
            size: 1200,
          });
          out[l.id] = { url, png };
        } catch { /* skip this location */ }
      }
      setClinicQrs(out);
    })();
  }, [doctor, isTurbo, origin]);

  const downloadClinicQr = useCallback((loc) => {
    const entry = clinicQrs[loc.id];
    if (!entry) return;
    const a = document.createElement('a');
    a.href = entry.png;
    a.download = `${doctor?.slug || 'doctor'}-checkin-${(loc.name || loc.id).replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  }, [clinicQrs, doctor?.slug]);

  const primaryCity = useMemo(() => {
    return (Array.isArray(doctor?.locations) && doctor.locations[0]?.city) || '';
  }, [doctor]);

  const downloadPngAt = useCallback(async (sizePx) => {
    if (!doctor) return;
    try {
      const dataUrl = await QRCode.toDataURL(siteUrl, { width: sizePx, margin: 2, errorCorrectionLevel: 'Q', color: { dark: '#0f172a', light: '#ffffff' } });
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `${doctor.slug}-qr-${sizePx}.png`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { setMsg(e.message); }
  }, [doctor, siteUrl]);

  // Stage palette + small helpers — keep parity with DoctorWebsiteMockB so
  // the printed poster visually matches the doctor's public site.
  const stage = useMemo(() => {
    const y = Number(doctor?.experience_years || 0);
    if (y <= 3)  return { id: '01', accent: '#0EA5E9', dark: '#075985', soft: '#E0F2FE', label: 'First Practice' };
    if (y <= 10) return { id: '02', accent: '#10B981', dark: '#065F46', soft: '#D1FAE5', label: 'Established Practice' };
    if (y <= 20) return { id: '03', accent: '#F59E0B', dark: '#92400E', soft: '#FEF3C7', label: 'Mastery & Innovation' };
    if (y <= 30) return { id: '04', accent: '#8B5CF6', dark: '#5B21B6', soft: '#EDE9FE', label: 'Public Footprint' };
    return        { id: '05', accent: '#F43F5E', dark: '#9F1239', soft: '#FFE4E6', label: 'Practicing Emeritus' };
  }, [doctor?.experience_years]);

  // Hex (#RRGGBB) → [r,g,b] for jsPDF setFillColor / setTextColor.
  const hex2rgb = (hex) => {
    const h = (hex || '#000000').replace('#', '');
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  };

  // Pre-fetch the doctor's photo and convert to a base64 dataURL so jsPDF
  // can embed it directly. Returns null on any failure (poster still prints
  // without the photo). CORS-safe: backend serves image with permissive CORS.
  const photoSrcAbs = useMemo(() => {
    if (!doctor?.photo_url) return null;
    const back = process.env.REACT_APP_BACKEND_URL || '';
    return doctor.photo_url.startsWith('/api/') ? `${back}${doctor.photo_url}` : doctor.photo_url;
  }, [doctor?.photo_url]);

  const fetchPhotoDataURL = useCallback(async () => {
    if (!photoSrcAbs) return null;
    try {
      const res = await fetch(photoSrcAbs, { credentials: 'omit', mode: 'cors' });
      if (!res.ok) return null;
      const blob = await res.blob();
      return await new Promise((resolve) => {
        const r = new FileReader();
        r.onloadend = () => resolve(r.result || null);
        r.onerror = () => resolve(null);
        r.readAsDataURL(blob);
      });
    } catch { return null; }
  }, [photoSrcAbs]);

  const downloadPoster = useCallback(async () => {
    if (!doctor || !mainQr || !vcardQr) return;
    setGenerating(true);
    try {
      // A4 portrait: 210 x 297 mm
      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
      const W = 210, H = 297;
      const [ar, ag, ab] = hex2rgb(stage.accent);
      const [dr, dg, dbb] = hex2rgb(stage.dark);
      const [sr, sg, sb] = hex2rgb(stage.soft);
      const firstName = (doctor.name || '').replace(/^Dr\.?\s*/i, '').split(' ')[0] || 'Doctor';
      const photoDataUrl = await fetchPhotoDataURL();

      // === HEADER BAND — stage-coloured 70mm strip ===
      pdf.setFillColor(sr, sg, sb);
      pdf.rect(0, 0, W, 70, 'F');
      // Thin accent edge at the bottom of the band
      pdf.setFillColor(ar, ag, ab);
      pdf.rect(0, 68, W, 2, 'F');

      // First-name eyebrow ("DR. CHETNA'S CLINIC") — bold, all-caps, accent dark
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(dr, dg, dbb);
      pdf.setFontSize(11);
      pdf.text(`DR. ${firstName.toUpperCase()}'S CLINIC`, 20, 18);

      // Full name in big serif-ish (helvetica bold is the closest jsPDF
      // built-in to a display serif without embedding a custom font).
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(15, 23, 42);
      pdf.setFontSize(30);
      pdf.text(doctor.name || '', 20, 36);

      // Qualifications + reg + tagline stack (left column)
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(71, 85, 105);
      pdf.setFontSize(11);
      if (doctor.qualifications) pdf.text(doctor.qualifications, 20, 45);

      // Affiliations sub-heading (e.g. "Consultant Cardiologist, HeartCare Clinic")
      let yCursor = doctor.qualifications ? 52 : 45;
      if (doctor.affiliations) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.setTextColor(51, 65, 85);
        pdf.text(pdf.splitTextToSize(doctor.affiliations, 130), 20, yCursor);
        yCursor += 7;
      }

      if (doctor.registration_number) {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(100, 116, 139);
        pdf.text(`NMC / State Medical Council Reg: ${doctor.registration_number}`, 20, yCursor);
        yCursor += 8;
      }

      if (doctor.tagline) {
        pdf.setFont('helvetica', 'italic');
        pdf.setFontSize(10);
        pdf.setTextColor(71, 85, 105);
        pdf.text(pdf.splitTextToSize(`"${doctor.tagline}"`, 130), 20, yCursor);
      }

      // === DOCTOR PHOTO — top-right circular thumbnail (~30mm) ===
      // jsPDF can't natively clip to a circle, but a white-bordered square
      // with rounded corners on top of the band reads as a "polaroid" tile,
      // which is fine for print.
      if (photoDataUrl) {
        const photoX = W - 50, photoY = 12, photoSize = 38;
        // White card behind the photo (gives a print-safe matte border)
        pdf.setFillColor(255, 255, 255);
        pdf.roundedRect(photoX - 2, photoY - 2, photoSize + 4, photoSize + 4, 3, 3, 'F');
        try {
          pdf.addImage(photoDataUrl, 'JPEG', photoX, photoY, photoSize, photoSize);
        } catch {
          // Some images may fail (e.g. unsupported format) — fall back silently
        }
        // Stage label under the photo
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.setTextColor(dr, dg, dbb);
        pdf.text(stage.label.toUpperCase(), photoX + photoSize / 2, photoY + photoSize + 6, { align: 'center' });
      } else {
        // Fallback: stage label only, top-right
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.setTextColor(dr, dg, dbb);
        pdf.text(stage.label.toUpperCase(), W - 20, 22, { align: 'right' });
      }

      // === MAIN QR ===
      pdf.addImage(mainQr, 'PNG', 55, 82, 100, 100);

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(15);
      pdf.setTextColor(15, 23, 42);
      pdf.text(`Scan to open Dr. ${firstName}'s website`, 105, 195, { align: 'center' });

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(100, 116, 139);
      pdf.text('Book appointments · See procedures · Read articles · Save my contact', 105, 202, { align: 'center' });

      // Clinic name strip — first 2 locations on this poster
      const locs = Array.isArray(doctor.locations) ? doctor.locations.slice(0, 2) : [];
      if (locs.length) {
        pdf.setFillColor(sr, sg, sb);
        pdf.roundedRect(20, 207, W - 40, 6 + locs.length * 5, 2, 2, 'F');
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.setTextColor(dr, dg, dbb);
        pdf.text('AT', 24, 211);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(15, 23, 42);
        pdf.setFontSize(9);
        locs.forEach((loc, i) => {
          const line = `${loc.name || 'Clinic'} · ${[loc.address, loc.city].filter(Boolean).join(', ')}`;
          pdf.text(pdf.splitTextToSize(line, W - 50), 32, 211 + i * 5);
        });
      }

      // === BOTTOM ROW — vCard QR + offline save copy ===
      const bottomY = 207 + (locs.length ? 6 + locs.length * 5 + 8 : 8);
      pdf.addImage(vcardQr, 'PNG', 20, bottomY, 38, 38);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.setTextColor(15, 23, 42);
      pdf.text('Save my contact (works offline)', 63, bottomY + 8);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(100, 116, 139);
      pdf.text(pdf.splitTextToSize('No internet needed. Scan to save my number, WhatsApp & clinic addresses straight to your phone.', 125), 63, bottomY + 14);

      if (doctor.phone) {
        pdf.setFontSize(10);
        pdf.setTextColor(15, 23, 42);
        pdf.text(`Call: ${doctor.phone}`, 63, bottomY + 32);
      }

      // === FOOTER — branded lockup ===
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(148, 163, 184);
      pdf.text(`Dr. ${firstName}'s Website  ·  Powered by DocSite Platform (TM)`, 105, 285, { align: 'center' });
      pdf.setFontSize(8);
      pdf.text(siteUrlClean, 105, 291, { align: 'center' });

      pdf.save(`${doctor.slug}-clinic-poster.pdf`);
    } catch (e) {
      setMsg('PDF failed: ' + e.message);
    } finally {
      setGenerating(false);
    }
  }, [doctor, mainQr, vcardQr, siteUrlClean, stage, fetchPhotoDataURL]);

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(''), 1500);
    } catch {}
  };

  if (!isTurbo) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center" data-testid="clinic-kit-locked">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mx-auto mb-4">
          <Crown size={24} className="text-white" />
        </div>
        <h3 className="text-xl font-medium text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Clinic Kit — TurboCharge only</h3>
        <p className="text-sm text-slate-500 max-w-md mx-auto mb-5">
          Printable A4 clinic poster, sticker pack, and an offline-ready QR that opens your website even when patients have no internet. Available on the <b>TurboCharge Your Visibility</b> plan.
        </p>
        <a href="/upgrade" className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-sm font-medium hover:shadow-lg transition-shadow" data-testid="clinic-kit-upgrade-cta">
          <Sparkles size={14} /> Upgrade to TurboCharge
        </a>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clinic-kit-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-medium text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Clinic Kit</h2>
          <p className="text-sm text-slate-500 max-w-xl">
            Print-ready QR poster + sticker pack. Patients scan in your clinic, your DocSite page opens instantly — and works offline once they've visited once.
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid="clinic-kit-turbo-badge">
          <CheckCircle size={12} /> Active · TurboCharge
        </span>
      </div>

      {msg && <div className="text-sm bg-red-50 text-red-700 border border-red-200 rounded-xl px-4 py-3">{msg}</div>}

      {/* Preview + downloads */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Main QR */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="text-base font-medium text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <QrCode size={16} className="inline mr-2 text-sky-600" />Main Website QR
          </h3>
          <p className="text-xs text-slate-500 mb-4">Scans open <code className="bg-slate-100 px-1.5 py-0.5 rounded">{siteUrl}</code></p>
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 flex items-center justify-center min-h-[200px]">
            {mainQr ? (
              <img src={mainQr} alt="Main site QR" className="w-48 h-48" data-testid="main-qr-preview" />
            ) : (
              <Loader2 size={20} className="animate-spin text-slate-400" />
            )}
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <button onClick={() => downloadPngAt(600)} disabled={!mainQr} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 disabled:opacity-50" data-testid="download-qr-600">
              <Download size={12} />PNG 600px
            </button>
            <button onClick={() => downloadPngAt(1200)} disabled={!mainQr} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 disabled:opacity-50" data-testid="download-qr-1200">
              <Download size={12} />PNG 1200px
            </button>
            <button onClick={() => downloadPngAt(2400)} disabled={!mainQr} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 disabled:opacity-50" data-testid="download-qr-2400">
              <Download size={12} />PNG 2400px (print)
            </button>
            <button onClick={() => copyText(siteUrl, 'url')} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-lg" data-testid="copy-qr-url">
              {copied === 'url' ? <CheckCircle size={12} className="text-emerald-500" /> : <Copy size={12} />}{copied === 'url' ? 'Copied' : 'Copy URL'}
            </button>
          </div>
        </div>

        {/* vCard */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="text-base font-medium text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Phone size={16} className="inline mr-2 text-emerald-600" />Save Contact QR
          </h3>
          <p className="text-xs text-slate-500 mb-4">Works <b>100% offline</b> — saves phone, WhatsApp, clinic address on scan</p>
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 flex items-center justify-center min-h-[200px]">
            {vcardQr ? (
              <img src={vcardQr} alt="vCard QR" className="w-40 h-40" data-testid="vcard-qr-preview" />
            ) : (
              <Loader2 size={20} className="animate-spin text-slate-400" />
            )}
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <a href={vcardQr || '#'} download={`${doctor?.slug || 'doctor'}-vcard-qr.png`} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200" data-testid="download-vcard-qr">
              <Download size={12} />Download PNG
            </a>
            <button onClick={() => copyText(vcardText, 'vcard')} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-lg" data-testid="copy-vcard">
              {copied === 'vcard' ? <CheckCircle size={12} className="text-emerald-500" /> : <Copy size={12} />}{copied === 'vcard' ? 'Copied' : 'Copy vCard text'}
            </button>
          </div>
        </div>
      </div>

      {/* Clinic Check-In QRs — one per location. Patient scans the QR on
          the specific clinic's wall/desk → form auto-tags that clinic.
          Each visit row lands on Patient Log already classified by clinic
          (no manual "where did you see them?" step). */}
      {Array.isArray(doctor?.locations) && doctor.locations.length > 0 && (
        <div className="bg-white rounded-2xl border-2 border-emerald-200 p-6" data-testid="clinic-checkin-qr-section">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
            <div>
              <h3 className="text-base font-medium text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Sparkles size={16} className="text-emerald-600" />
                Clinic Check-In QR
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500 text-white">New</span>
              </h3>
              <p className="text-xs text-slate-500 mt-1 max-w-xl">
                Print one per clinic. Patient scans in your consult room → name + mobile + gender → row appears live on your Patient Log + auto-SMS feedback link. <b>Each clinic gets its own QR</b> so visits are tagged to the right location without you picking anything.
              </p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
            {doctor.locations.map(loc => {
              const entry = clinicQrs[loc.id];
              return (
                <div key={loc.id} className="rounded-xl border border-emerald-100 bg-emerald-50/30 p-4 flex flex-col" data-testid={`clinic-checkin-qr-${loc.id}`}>
                  <div className="flex items-start gap-2 mb-3">
                    <MapPin size={14} className="text-emerald-700 mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-900 truncate">{loc.name || 'Clinic'}</p>
                      <p className="text-[11px] text-slate-500 truncate">{loc.city || ''}{loc.city && loc.state ? ', ' : ''}{loc.state || ''}</p>
                    </div>
                    {/* DocSite mark, top-right aligned inside the card border */}
                    <img
                      src="/docsite-logo.png"
                      alt="DocSite"
                      className="w-9 h-9 object-contain shrink-0 -mt-0.5"
                      data-testid={`clinic-checkin-card-logo-${loc.id}`}
                    />
                  </div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-sky-700 text-center mb-1" data-testid={`clinic-checkin-header-${loc.id}`}>
                    Patient Self-Checkin QR
                  </p>
                  <p className="text-[11px] text-emerald-800 text-center mb-2 leading-tight" data-testid={`clinic-checkin-benefit-${loc.id}`}>
                    <span className="font-extrabold uppercase tracking-wide">DIGITAL HEALTH FILE</span>
                    <span className="font-medium"> for </span><span className="font-bold">same day</span><span className="font-medium"> CHECK-IN only. </span><span className="font-extrabold">FREE.</span>
                  </p>
                  <div className="bg-white rounded-lg border border-emerald-100 p-3 flex items-center justify-center mb-3 min-h-[140px]">
                    {entry?.png ? (
                      <img src={entry.png} alt={`Check-in QR for ${loc.name}`} className="w-40 h-auto" />
                    ) : (
                      <Loader2 size={18} className="animate-spin text-slate-400" />
                    )}
                  </div>
                  <div className="flex gap-2 mt-auto">
                    <button
                      onClick={() => downloadClinicQr(loc)}
                      disabled={!entry}
                      className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg"
                      data-testid={`download-clinic-checkin-${loc.id}`}
                    >
                      <Download size={11} />PNG
                    </button>
                    <button
                      onClick={() => copyText(entry?.url || '', `chk-${loc.id}`)}
                      disabled={!entry}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50"
                      data-testid={`copy-clinic-checkin-${loc.id}`}
                    >
                      {copied === `chk-${loc.id}` ? <CheckCircle size={11} className="text-emerald-500" /> : <Copy size={11} />}
                      {copied === `chk-${loc.id}` ? 'Copied' : 'URL'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-emerald-800/70 mt-4 flex items-center gap-1.5">
            <Sparkles size={11} />
            Print at A5 size (or smaller). Tape on the patient-facing side of your consult-desk or the chair-side wall.
          </p>
        </div>
      )}

      {/* Poster section */}
      <div className="bg-gradient-to-br from-sky-50 to-emerald-50 border border-sky-100 rounded-2xl p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-base font-medium text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <FileDown size={16} className="inline mr-2 text-sky-600" />A4 Printable Clinic Poster (PDF)
            </h3>
            <p className="text-xs text-slate-500 max-w-xl">
              Single-page portrait poster with your name, tagline, primary QR, and a secondary "Save contact" QR. Branded footer: <em>"{doctor?.name ? doctor.name + "'s" : "Your"} Website · Powered by DocSite Platform (TM)"</em>.
            </p>
          </div>
          <button
            onClick={downloadPoster}
            disabled={generating || !mainQr || !vcardQr}
            className="flex items-center gap-1.5 px-5 py-2.5 text-sm font-medium bg-sky-600 text-white rounded-xl hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="download-poster-pdf"
          >
            {generating ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}Download A4 Poster
          </button>
        </div>
        <div ref={posterPreviewRef} className="mt-5 grid md:grid-cols-[1fr_auto] gap-4 items-start">
          <ul className="text-xs text-slate-600 space-y-2">
            <li className="flex gap-2"><span className="text-emerald-500">✓</span> Scans open your website with lead source = Clinic QR (tracked separately in My Leads)</li>
            <li className="flex gap-2"><span className="text-emerald-500">✓</span> Patients who've visited once can re-scan offline — your website still opens (cached on their phone)</li>
            <li className="flex gap-2"><span className="text-emerald-500">✓</span> WhatsApp button on your site auto-prefills: <em>"Hi, I scanned your clinic QR and would like to book an appointment."</em></li>
            <li className="flex gap-2"><span className="text-emerald-500">✓</span> Secondary "Save contact" QR works 100% offline — patients save your number instantly</li>
          </ul>
          {mainQr && (
            <div className="bg-white rounded-xl border border-slate-200 p-3 w-40 text-center shadow-sm">
              <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">Preview</div>
              <img src={mainQr} alt="Preview" className="w-full" />
              <div className="text-[9px] text-slate-500 mt-1 truncate">{doctor?.name}</div>
            </div>
          )}
        </div>
      </div>

      {/* Quick links */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="text-sm font-medium text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Share assets</h3>
        <div className="grid md:grid-cols-3 gap-3 text-xs">
          <div className="flex items-start gap-2">
            <MapPin size={14} className="text-sky-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-slate-700">Tracked URL</p>
              <p className="text-slate-500 break-all">{siteUrl}</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <MessageCircle size={14} className="text-emerald-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-slate-700">Primary city</p>
              <p className="text-slate-500">{primaryCity || '—'}</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Phone size={14} className="text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-slate-700">Contact</p>
              <p className="text-slate-500">{doctor?.phone || '—'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
